import { TRPCClientError, type TRPCLink } from "@trpc/client";
import { observable } from "@trpc/server/observable";

import type { AppRouter } from "../../server/routers";
import { enqueueOperation, type OutboxOwner } from "./offline-outbox";

/**
 * Il punto unico di passaggio delle scritture.
 *
 * Sta sopra al link di rete, quindi ogni mutazione dell'app gli passa davanti, comprese
 * quelle che nessuno ha ancora scritto. Quando la rete manca l'azione non fallisce: viene
 * registrata sul dispositivo e la schermata riceve un risultato che dice "presa in carico".
 * La lettura non lo attraversa: le query restano quelle che sono, e chi apre una scheda
 * senza rete vede l'ultimo dato disponibile.
 */

/** Riconoscibile a occhio in un oggetto qualunque, e impossibile da confondere con un dato. */
export const OFFLINE_QUEUED = "__manutioInAttesaDiRete__" as const;

export type OfflineQueued = {
  readonly [OFFLINE_QUEUED]: true;
  operationId: string;
  label: string;
};

/**
 * Vero quando l'azione non e arrivata al server ma e al sicuro sul dispositivo.
 *
 * Serve alle sole schermate che leggono il risultato di una mutazione, per esempio per
 * prendere il numero assegnato dal server: quel numero, offline, non esiste ancora.
 */
export function isOfflineQueued(value: unknown): value is OfflineQueued {
  return typeof value === "object" && value !== null && OFFLINE_QUEUED in value;
}

let owner: OutboxOwner | null = null;

/** L'app dichiara qui di chi e la coda, appena conosce la sessione. */
export function setOutboxOwner(next: OutboxOwner | null) {
  owner = next;
}

export function currentOutboxOwner() {
  return owner;
}

/**
 * Quello che non si mette in coda, e perche.
 *
 * `auth`: un accesso o un invito non si rimandano, o si fanno adesso o non hanno senso,
 * e una password non deve restare scritta sul dispositivo in attesa di rete.
 * `platform`: la console di piattaforma non e lavoro di campo, si amministra da collegati.
 */
const NEVER_QUEUED = ["auth", "platform"];

function queueable(path: string) {
  return !NEVER_QUEUED.includes(path.split(".")[0] ?? "");
}

/**
 * Vero se la richiesta non e mai arrivata al server.
 *
 * Un rifiuto del server porta con se la sua forma di errore, e quello non si mette in coda:
 * un dato non valido resta non valido anche fra un'ora, e va detto subito a chi lo ha scritto.
 */
export function isTransportFailure(error: unknown) {
  if (!(error instanceof TRPCClientError)) return false;
  return error.data == null;
}

const labels: Record<string, string> = {
  catalog: "Voce di catalogo",
  chat: "Messaggio",
  clients: "Cliente",
  feedback: "Segnalazione",
  interventions: "Intervento",
  materials: "Materiale",
  measurements: "Misurazione",
  modules: "Moduli",
  pools: "Registro piscine",
  purchasing: "Acquisti",
  scheduling: "Programma",
  sites: "Struttura",
  suppliers: "Fornitore",
  support: "Assistenza",
  team: "Squadra",
  workLog: "Registro ore",
};

/** Frase breve per chi guarda la coda: il percorso tecnico non dice niente a un operaio. */
export function describeOperation(path: string) {
  const [area, ...rest] = path.split(".");
  return `${labels[area ?? ""] ?? area ?? "Operazione"}: ${rest.join(" ") || "aggiornamento"}`;
}

function queuedResult(operationId: string, label: string): OfflineQueued {
  return { [OFFLINE_QUEUED]: true, operationId, label };
}

/**
 * Il link. Mettilo prima del link di rete, mai dopo.
 */
export function offlineLink(
  options: { online?: () => boolean } = {},
): TRPCLink<AppRouter> {
  const online = options.online ?? (() => (typeof navigator === "undefined" ? true : navigator.onLine));

  return () => ({ op, next }) => observable((observer) => {
    const label = describeOperation(op.path);

    function queue(fallback: unknown) {
      const current = owner;
      if (!current) { observer.error(fallback as TRPCClientError<AppRouter>); return; }
      void enqueueOperation({
        companyId: current.companyId,
        userId: current.userId,
        kind: "trpc",
        target: op.path,
        // Serializzato adesso, con lo stesso JSON che sarebbe partito: al ritorno della
        // rete si rimanda esattamente quello, senza ricostruire niente.
        body: JSON.stringify(op.input ?? null),
        label,
      }).then((operation) => {
        observer.next({ result: { data: queuedResult(operation.id, label) } });
        observer.complete();
      }).catch(() => { observer.error(fallback as TRPCClientError<AppRouter>); });
    }

    // Rete gia spenta: non si tenta nemmeno, si registra e si va avanti.
    if (op.type === "mutation" && queueable(op.path) && owner && !online()) {
      queue(new TRPCClientError("Rete non disponibile."));
      return;
    }

    const subscription = next(op).subscribe({
      next: (value) => observer.next(value),
      complete: () => observer.complete(),
      error: (error) => {
        if (op.type !== "mutation" || !queueable(op.path) || !owner || !isTransportFailure(error)) {
          observer.error(error);
          return;
        }
        queue(error);
      },
    });
    return () => { subscription.unsubscribe(); };
  });
}
